'use client';

import { usePathname } from 'next/navigation';
import type { ReactNode } from 'react';
import { Box } from '@terra/ui-styled-system';
import { AuthGuard } from './AuthGuard';
import { AppLayout } from './layout/AppLayout';
import { useTrackingListeners } from '../hooks/useTrackingListeners';
import { useActivityTracker } from '../hooks/useActivityTracker';
import { useAutoSelectCompany } from '../hooks/useAutoSelectCompany';
import { useSyncSelectedTarget } from '../hooks/useSyncSelectedTarget';
import { useSyncApplicationGlobalHeadersToProxy } from '../hooks/useApplicationGlobalHeaders';
import { useValidateProjectTenant } from '../hooks/useValidateProjectTenant';
import { useSyncMtlsSettings } from '../hooks/useSyncMtlsSettings';
import {
  isTerraProxyAuthPublicPathname,
  isTerraProxyE2eHarnessPublicPathname,
} from '../middlewares/auth/public-routes';
import { getDetachedWindowMode } from '../utils/detachedMode';
import { DetachedTabLayout } from './layout/DetachedTabLayout';
import { WorkspacePickerScreen } from './workspace/WorkspacePickerScreen';
import { useWorkspaceBootstrap } from '../hooks/useWorkspaceBootstrap';
import { useWorkspaceTargetSync } from '../hooks/useWorkspaceTargetSync';
import {
  useIsWorkspacePicker,
  useIsWorkspaceResolving,
} from '../store/workspaceStore';

type AppLayoutProps = {
  children: ReactNode;
};

// Minimal draggable top bar for public routes
function DraggableTopBar() {
  return (
    <Box
      h="9"
      borderBottomWidth="1px"
      borderColor="border.subtle"
      px="4"
      pl="20" // Space for macOS traffic light buttons
      display="flex"
      alignItems="center"
      bg="bg.subtle"
      position="fixed"
      top="0"
      left="0"
      right="0"
      zIndex={1000}
      style={
        {
          WebkitAppRegion: 'drag',
        } as React.CSSProperties
      }
    />
  );
}

export function AuthLayout({ children }: AppLayoutProps) {
  const pathname = usePathname();

  // Initialize tracking listeners (must be called unconditionally per React Rules of Hooks)
  useTrackingListeners();

  // Initialize activity tracker for session inactivity monitoring
  useActivityTracker();

  useWorkspaceBootstrap();
  useAutoSelectCompany();
  useSyncSelectedTarget();
  useWorkspaceTargetSync();
  useSyncApplicationGlobalHeadersToProxy();
  useValidateProjectTenant();

  // Keep the proxy's mTLS registry in sync with the user's Client TLS settings.
  // Lives here (not in the settings tab) because the tab uses lazyMount +
  // unmountOnExit, so a tab-scoped sync would overwrite the launcher's
  // per-session "no client TLS" override every time the tab is opened.
  useSyncMtlsSettings();

  const isPublicRoute =
    isTerraProxyAuthPublicPathname(pathname) ||
    isTerraProxyE2eHarnessPublicPathname(pathname);
  const detachedMode = getDetachedWindowMode();
  const isWorkspaceResolving = useIsWorkspaceResolving();
  const isWorkspacePicker = useIsWorkspacePicker();

  if (!isPublicRoute && detachedMode.type !== 'main') {
    return (
      <AuthGuard>
        <DetachedTabLayout mode={detachedMode} />
      </AuthGuard>
    );
  }

  if (isPublicRoute) {
    return (
      <Box h="100vh" w="100vw" position="relative">
        <DraggableTopBar />
        <Box h="100vh" pt="9" overflow="auto">
          {children}
        </Box>
      </Box>
    );
  }

  if (isWorkspaceResolving) {
    return <DraggableTopBar />;
  }

  if (isWorkspacePicker) {
    return (
      <AuthGuard>
        <DraggableTopBar />
        <WorkspacePickerScreen />
      </AuthGuard>
    );
  }

  return (
    // For protected routes, wrap with auth guard and VSCode-like layout
    <AuthGuard>
      <AppLayout>{children}</AppLayout>
    </AuthGuard>
  );
}
